// src/features/stock/api/attributeApi.ts

import { fetchData } from '../../../../utils/fetch';

export interface Attribute {
  id_attribute: number;
  attribute_name: string;
  timestamp: string;
}

export interface NewAttributePayload {
  id_category: number;
  attribute_name: string;
  attribute_value?: string;
}

export interface DeleteAttributePayload {
  attribute_id: number;
}

export async function deleteAttributeApi(
  payload: DeleteAttributePayload
): Promise<void> {
  await fetchData('/service_product_manager/delete_attribute', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id_attribute: payload.attribute_id }),
  });
}

export async function fetchAttributesApi(): Promise<Attribute[]> {
  const raw = await fetchData('/service_product_manager/get_attributes', { method: 'GET' });

  let arr: any[] = [];
  if (Array.isArray(raw)) {
    arr = raw as any[];
  } else if (raw != null && Array.isArray((raw as any).data)) {
    arr = (raw as any).data;
  } else if (raw != null && Array.isArray((raw as any).attributes)) {
    arr = (raw as any).attributes;
  }

  return arr.map((rec) => ({
    id_attribute: rec.attribute_id ?? rec.id_attribute,
    attribute_name: rec.attribute_name,
    timestamp: rec.timestamp?.split('.')[0] ?? '',
  }));
}

export async function createAttributeApi(
  payload: NewAttributePayload
): Promise<Attribute> {
  const response = await fetchData('/service_product_manager/create_attribute', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  // 1) Backend returns { created: { attribute_id: … } }
  if (response.created && typeof response.created.attribute_id === 'number') {
    return {
      id_attribute: response.created.attribute_id,
      attribute_name: response.created.attribute_name,
      timestamp: response.created.timestamp || '',
    };
  }

  // 2) Or { result: { id_attribute: … } }
  if (response.result && typeof response.result.id_attribute === 'number') {
    return {
      id_attribute: response.result.id_attribute,
      attribute_name: response.result.attribute_name,
      timestamp: response.result.timestamp || '',
    };
  }

  // 3) Or top‐level { attribute_id, attribute_name, execution_time_ms }
  if (
    typeof (response as any).attribute_id === 'number' &&
    typeof (response as any).attribute_name === 'string'
  ) {
    return {
      id_attribute: (response as any).attribute_id,
      attribute_name: (response as any).attribute_name,
      timestamp: (response as any).timestamp ?? '',
    };
  }

  console.warn('[createAttributeApi] Unexpected response shape:', response);
  throw new Error('Could not parse newly‐created attribute.');
}

/**
 * Each object in the backend’s JSON array will contain at least:
 *   • attribute_id (or id_attribute)
 *   • attribute_name
 *   • timestamp
 *
 * We call POST /outcomes/get_outcome_run with:
 *   {
 *     id_service: 7,
 *     id_category: 8,      // “8” means “attributes linked to a product”
 *     id_type: <productId>
 *   }
 *
 * Since CRA is proxying “/outcomes” → your backend, we hit this URL via:
 *   '/frontend_react/outcomes/get_outcome_run'
 */
export async function fetchProductAttributesApi(
  productId: number
): Promise<{
  id_attribute: number;
  attribute_name: string;
  timestamp: string;
}[]> {
  // 1) Build the payload
  const payload = {
    id_service: 7,
    id_category: 8,
    id_type: productId,
  };

  // 2) POST to the proxy path “/frontend_react/outcomes/get_outcome_run”
  const raw = await fetchData('/frontend_react/outcomes/get_outcome_run', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  // 3) Normalize whatever array shape the backend returned
  //    (it might be top-level array, or under .data, or under .attributes, etc.)
  let arr: any[] = [];
  if (Array.isArray(raw)) {
    arr = raw as any[];
  } else if (raw != null && Array.isArray((raw as any).data)) {
    arr = (raw as any).data;
  } else if (raw != null && Array.isArray((raw as any).attributes)) {
    arr = (raw as any).attributes;
  } else {
    console.warn('[fetchProductAttributesApi] unexpected response shape:', raw);
    return [];
  }

  // 4) Map each record into { id_attribute, attribute_name, timestamp }
  return arr.map((rec) => ({
    id_attribute: rec.attribute_id ?? rec.id_attribute,
    attribute_name: rec.attribute_name,
    timestamp: rec.timestamp?.split('.')[0] ?? '',
  }));
}